import { getScrollTop, getScrollEventTarget, scrollContainerMediaQuery } from '@theme/scroll-container';

const HEADER_SELECTOR = '#header-component';
const COMPACT_ROW_SELECTOR = '[data-atc-compact-row]';
const NORMAL_ROWS_OUTER_SELECTOR = '.atc-header__normal-rows-outer';
const NORMAL_ROWS_INNER_SELECTOR = '.atc-header__normal-rows-inner';
const COMPACT_OUTER_SELECTOR = '.atc-header__compact-outer';

/** Extra scroll distance (px) past the natural header height before compact activates */
const COMPACT_SCROLL_THRESHOLD_EXTRA = 80;

/** @param {HTMLElement} header */
const getAutoMenuStyle = (header) => {
  const isTouchDevice = 'ontouchstart' in window && navigator.maxTouchPoints > 0;
  const overflowList = header.querySelector('overflow-list');
  const hasReachedMinimum = overflowList && overflowList.hasAttribute('minimum-reached');

  return isTouchDevice || hasReachedMinimum ? 'drawer' : 'menu';
};

class AtcHeaderCompactController {
  #isTransitioning = false;
  #transitionTimeout = null;

  /** @param {HTMLElement} header */
  constructor(header) {
    this.header = header;
    this.compactRow = /** @type {HTMLElement | null} */ (header.querySelector(COMPACT_ROW_SELECTOR));
    this.normalRowsOuter = /** @type {HTMLElement | null} */ (header.querySelector(NORMAL_ROWS_OUTER_SELECTOR));
    this.normalRowsInner = /** @type {HTMLElement | null} */ (header.querySelector(NORMAL_ROWS_INNER_SELECTOR));
    this.compactOuter = /** @type {HTMLElement | null} */ (header.querySelector(COMPACT_OUTER_SELECTOR));
    this.normalRows = /** @type {HTMLElement[]} */ ([
      ...header.querySelectorAll('.header__row:not(.atc-header__compact-row), .header__navigation-bar-row'),
    ]);
    this.raf = 0;
    this.scrollThreshold = 0;
    /** @type {EventTarget | null} */
    this.scrollTarget = null;
    this.menuObserver = new MutationObserver(() => this.applyDesktopMenuType());
    this.stateObserver = new MutationObserver(() => this.queueUpdate());
    this.onScroll = () => this.queueUpdate();
    this.onResize = () => this.#handleResize();
    this.onBreakpointChange = () => this.#rebindScrollTarget();
  }

  connect() {
    this.applyDesktopMenuType();
    this.scrollThreshold = this.header.offsetHeight + COMPACT_SCROLL_THRESHOLD_EXTRA;
    this.menuObserver.observe(this.header, {
      attributes: true,
      attributeFilter: ['data-atc-desktop-menu-type', 'data-menu-style'],
    });
    this.stateObserver.observe(this.header, {
      attributes: true,
      attributeFilter: [
        'data-atc-sticky-compact-on-scroll',
        'data-sticky-state',
        'data-scroll-direction',
        'sticky',
      ],
    });
    // The scroll container switches between `.page-wrapper` (desktop, ≥990px) and
    // `document` (mobile) — see @theme/scroll-container. Rebind whenever that breakpoint
    // is crossed, matching HeaderComponent's own #handleBreakpointChange in header.js.
    this.#rebindScrollTarget();
    scrollContainerMediaQuery.addEventListener('change', this.onBreakpointChange);
    window.addEventListener('resize', this.onResize);
    this.queueUpdate();
  }

  #rebindScrollTarget() {
    if (this.scrollTarget) {
      this.scrollTarget.removeEventListener('scroll', this.onScroll);
    }
    this.scrollTarget = getScrollEventTarget();
    this.scrollTarget.addEventListener('scroll', this.onScroll, { passive: true });
    this.queueUpdate();
  }

  applyDesktopMenuType() {
    const nextMenuStyle = this.header.dataset.atcDesktopMenuType === 'drawer' ? 'drawer' : getAutoMenuStyle(this.header);

    if (this.header.dataset.menuStyle !== nextMenuStyle) {
      this.header.dataset.menuStyle = nextMenuStyle;
    }
  }

  queueUpdate() {
    this.applyDesktopMenuType();
    if (this.raf) return;
    this.raf = requestAnimationFrame(() => {
      this.raf = 0;
      this.updateCompactState();
    });
  }

  updateCompactState() {
    if (this.header.dataset.atcStickyCompactOnScroll !== 'true') {
      this.setCompactActive(false);
      return;
    }

    // Don't interrupt an in-progress transition — re-evaluation runs on transitionend
    if (this.#isTransitioning) return;

    // Compact header is desktop-only — mobile is already minimal
    if (window.innerWidth < 750) {
      this.setCompactActive(false);
      return;
    }

    // Compact header only activates in 'always' sticky mode
    const stickyMode = this.header.getAttribute('sticky');
    if (stickyMode !== 'always') {
      this.setCompactActive(false);
      return;
    }

    const stickyState = this.header.dataset.stickyState;
    const isCurrentlyActive = this.header.dataset.atcStickyCompactActive === 'true';

    if (isCurrentlyActive) {
      // Deactivate only when the header is no longer sticky — i.e. the user has
      // scrolled back to the top of the page. Using stickyState (IntersectionObserver)
      // rather than scrollY avoids interference from CSS Scroll Anchoring, which
      // adjusts scrollY when the header height changes and would otherwise cause
      // oscillation at the threshold boundary.
      this.setCompactActive(stickyState === 'active');
    } else {
      // Activate once the header is sticky and the user has scrolled far enough
      // past the natural header height (COMPACT_SCROLL_THRESHOLD_EXTRA provides
      // a small delay before the compact transition fires).
      this.setCompactActive(stickyState === 'active' && getScrollTop() > this.scrollThreshold);
    }
  }

  /** @param {boolean} active */
  setCompactActive(active) {
    const wasActive = this.header.dataset.atcStickyCompactActive === 'true';
    if (wasActive === active) return;

    this.#isTransitioning = true;
    clearTimeout(this.#transitionTimeout);

    if (this.normalRowsInner) {
      this.normalRowsInner.classList.add('atc--is-transitioning');
    }

    const onTransitionDone = () => {
      clearTimeout(this.#transitionTimeout);
      this.#transitionTimeout = null;
      this.normalRowsInner?.classList.remove('atc--is-transitioning');
      this.#isTransitioning = false;
      // Re-evaluate after animation — catches scroll changes that occurred mid-transition
      this.queueUpdate();
    };

    if (this.normalRowsOuter) {
      this.normalRowsOuter.addEventListener('transitionend', onTransitionDone, { once: true });
    }

    // Fallback in case transitionend does not fire (e.g. prefers-reduced-motion: reduce)
    this.#transitionTimeout = setTimeout(() => {
      this.#transitionTimeout = null;
      if (!this.#isTransitioning) return;
      this.normalRowsInner?.classList.remove('atc--is-transitioning');
      this.#isTransitioning = false;
      this.queueUpdate();
    }, 600);

    this.header.dataset.atcStickyCompactActive = String(active);

    if (this.compactRow) {
      this.compactRow.toggleAttribute('inert', !active);
      this.compactRow.setAttribute('aria-hidden', String(!active));
    }

    for (const row of this.normalRows) {
      row.toggleAttribute('inert', active);
      row.setAttribute('aria-hidden', String(active));
    }
  }

  #handleResize() {
    this.scrollThreshold = this.header.offsetHeight + COMPACT_SCROLL_THRESHOLD_EXTRA;
    this.queueUpdate();
  }
}

const initAtcHeaderCompact = () => {
  const header = /** @type {HTMLElement | null} */ (document.querySelector(HEADER_SELECTOR));
  if (!header || header.dataset.atcCompactControllerInitialized === 'true') return;
  header.dataset.atcCompactControllerInitialized = 'true';
  new AtcHeaderCompactController(header).connect();
};

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initAtcHeaderCompact, { once: true });
} else {
  initAtcHeaderCompact();
}

document.addEventListener('shopify:section:load', initAtcHeaderCompact);
